import { UnreadablePhotoError } from '../../domain/drafts';
import { isDomainError } from '../../domain/errors';

/** The transport call a failure came from; some answers mean different things per step. */
export type SyncStep =
  | 'upload'
  | 'create'
  | 'patch'
  | 'addPhotos'
  | 'deletePhoto'
  | 'reorder'
  | 'deleteListing'
  | 'deletePendingImage';

/**
 * FR-009's kinds, as the engine acts on them (research R6).
 * - `transient`: retry with backoff. `engine` scope waits everything out (the
 *   network or the service is the problem); `draft` scope backs off only the
 *   draft that drew an answer nobody expected, so it can't hold up the rest.
 * - `needsPerson`: pause every draft until a trigger finds it resolved.
 * - `permanentPhoto` / `permanentCollection`: that draft waits for the person.
 * - `repairUploads`: uploads expired before they were attached; upload them
 *   again under the same ids.
 * - `gone`: the listing was deleted elsewhere after it reached the service;
 *   removal wins.
 */
export type SyncFailure =
  | { kind: 'transient'; scope: 'engine' | 'draft' }
  | { kind: 'needsPerson'; need: 'signIn' | 'verification' }
  | { kind: 'permanentPhoto'; reason: 'unreadable' | 'rejected' }
  | { kind: 'permanentCollection' }
  | { kind: 'repairUploads' }
  | { kind: 'gone' };

const ENGINE: SyncFailure = { kind: 'transient', scope: 'engine' };
const DRAFT: SyncFailure = { kind: 'transient', scope: 'draft' };

/** The one place a failed sync call is sorted into what the engine does next. */
export function classifySyncFailure(error: unknown, step: SyncStep): SyncFailure {
  if (error instanceof UnreadablePhotoError)
    return { kind: 'permanentPhoto', reason: 'unreadable' };
  if (!isDomainError(error)) return ENGINE;

  switch (error.kind) {
    case 'failure':
    case 'throttled':
      return ENGINE;
    case 'notAuthenticated':
      return { kind: 'needsPerson', need: 'signIn' };
    case 'verificationRequired':
      return { kind: 'needsPerson', need: 'verification' };
    case 'notPermitted':
      // Django answers a stale CSRF token (the session changed under the page)
      // with a plain 403; only signing in again fixes it.
      if (error.message.startsWith('CSRF Failed')) return { kind: 'needsPerson', need: 'signIn' };
      return step === 'create' ? { kind: 'permanentCollection' } : DRAFT;
    case 'listingDeleted':
      // A repeated create whose listing was deleted since (on another device,
      // after an answer this one never got): removal wins.
      return { kind: 'gone' };
    case 'notFound':
      // A create answers 404 `collection_unavailable` for a collection that is
      // gone or hidden.
      if (step === 'create') return { kind: 'permanentCollection' };
      if (step === 'patch' || step === 'addPhotos' || step === 'reorder') return { kind: 'gone' };
      return DRAFT;
    case 'invalid':
      return invalid(error.message, error.fields, step);
    default:
      return DRAFT;
  }
}

function invalid(message: string, fields: Record<string, string[]>, step: SyncStep): SyncFailure {
  if (/too many pending images/i.test(message)) return ENGINE;
  if (fields.pendingImageIds && (step === 'create' || step === 'addPhotos')) {
    return { kind: 'repairUploads' };
  }
  // On an upload, any refusal but the id's is about the image itself.
  if (step === 'upload' && !fields.id) return { kind: 'permanentPhoto', reason: 'rejected' };
  return DRAFT;
}
